import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
  FileText,
  Calendar,
  User,
  Play,
  CheckSquare,
  Camera,
  Image,
  RefreshCw,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import BackButton from "../../components/BackButton";
import { Spinner, Alert, formatRupiah } from "../../components/ui";
import RatingForm from "../../components/RatingForm";
import { StarRating } from "../../components/StarRating";
import { useAuth } from "../../context/AuthContext";
import PaymentStatus from "../../components/PaymentStatus";

const STATUS_CONFIG = {
  PENDING_WORKER: { label: "Menunggu Pekerja", color: "bg-amber-50 text-amber-600", icon: Clock },
  ACCEPTED: { label: "Diterima", color: "bg-blue-50 text-blue-600", icon: CheckCircle2 },
  ON_PROGRESS: { label: "Berlangsung", color: "bg-brand-50 text-brand-600", icon: Clock },
  COMPLETED: { label: "Selesai", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  CONFIRMED: { label: "Dikonfirmasi", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak", color: "bg-red-50 text-red-600", icon: XCircle },
  CANCELLED: { label: "Dibatalkan", color: "bg-gray-50 text-gray-600", icon: XCircle },
};

export default function WorkerBookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [beforeFile, setBeforeFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [completionNote, setCompletionNote] = useState("");
  const [beforeUrl, setBeforeUrl] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [showCOForm, setShowCOForm] = useState(false);
  const [coDescription, setCODescription] = useState("");
  const [coAmount, setCOAmount] = useState("");
  const [coLoading, setCOLoading] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  const refreshBooking = async () => {
    const { data } = await api.get(`/api/bookings/${id}`);
    setBooking(data);
  };

  useEffect(() => {
    if (beforeFile) {
      const url = URL.createObjectURL(beforeFile);
      setBeforePreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setBeforePreview(null);
  }, [beforeFile]);

  useEffect(() => {
    if (proofFile) {
      const url = URL.createObjectURL(proofFile);
      setProofPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setProofPreview(null);
  }, [proofFile]);

  useEffect(() => {
    if (booking?.has_before_proof) {
      api.get(`/api/bookings/${id}/proof?type=before`, { responseType: "blob" })
        .then((r) => setBeforeUrl(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [booking?.has_before_proof, id]);

  useEffect(() => {
    if (booking?.has_proof && (booking.status === "COMPLETED" || booking.status === "CONFIRMED")) {
      api.get(`/api/bookings/${id}/proof?type=after`, { responseType: "blob" })
        .then((r) => setProofUrl(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [booking?.has_proof, booking?.status, id]);

  useEffect(() => {
    if (booking?.status === "CONFIRMED") {
      api.get(`/api/reviews/booking/${id}`)
        .then((r) => setReviews(r.data || []))
        .catch(() => {});
    }
  }, [booking?.status, id]);

  useEffect(() => {
    if (["ACCEPTED", "ON_PROGRESS", "COMPLETED", "CONFIRMED"].includes(booking?.status)) {
      api.get(`/api/change-orders/booking/${id}`)
        .then((r) => setChangeOrders(r.data || []))
        .catch(() => {});
    }
  }, [booking?.status, id]);

  useEffect(() => {
    if (booking?.status !== "COMPLETED" || !booking.auto_confirm_at) return;
    const t = setInterval(() => {
      const target = new Date(booking.auto_confirm_at).getTime();
      const next = Date.now();
      setNowTick(next);
      if (next >= target) refreshBooking().catch(() => {});
    }, 1000);
    return () => clearInterval(t);
  }, [booking?.status, booking?.auto_confirm_at, id]);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        await refreshBooking();
      } catch (err) {
        setError("Pesanan tidak ditemukan.");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const handleAction = async (action, body = {}) => {
    setActionLoading(true);
    setError(null);
    try {
      await api.post(`/api/bookings/${id}/${action}`, body);
      await refreshBooking();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async () => {
    if (!beforeFile) return;
    setActionLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("before_photo", beforeFile);
      await api.post(`/api/bookings/${id}/start`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshBooking();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  const handleProposeCO = async () => {
    if (!coDescription.trim() || !coAmount) return;
    setCOLoading(true);
    setError(null);
    try {
      await api.post(`/api/change-orders/booking/${id}`, {
        description: coDescription,
        additional_amount: parseInt(coAmount, 10),
      });
      const { data } = await api.get(`/api/change-orders/booking/${id}`);
      setChangeOrders(data || []);
      setCODescription("");
      setCOAmount("");
      setShowCOForm(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setCOLoading(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) return;
    setDisputeLoading(true);
    setError(null);
    try {
      await api.post(`/api/bookings/${id}/dispute`, { reason: disputeReason });
      await refreshBooking();
      setShowDisputeForm(false);
      setDisputeReason("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setDisputeLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!proofFile) return;
    setActionLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("after_photo", proofFile);
      if (completionNote) formData.append("completion_note", completionNote);
      await api.post(`/api/bookings/${id}/complete`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshBooking();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Spinner />;
  if (error && !booking) {
    return (
      <div className="animate-fade-up grid place-items-center">
        <div className="container space-y-6">
          <BackButton to="/worker/bookings" />
          <div className="card p-10 text-center">
            <p className="font-bold text-brand-800">{error || "Pesanan tidak ditemukan"}</p>
          </div>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.PENDING_WORKER;
  const StatusIcon = status.icon;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to="/worker/bookings" />

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-brand-800">Detail Pesanan</h1>
            <p className="text-brand-400 mt-1 text-sm">{booking.id?.slice(0, 8)}</p>
          </div>
          <span className={`chip ${status.color}`}>
            <StatusIcon className="h-4 w-4" /> {status.label}
          </span>
        </div>

        {error && <Alert kind="error">{error}</Alert>}

        {/* Info */}
        <div className="card p-6 space-y-4">
          <h2 className="text-brand-800 text-lg">Informasi Pesanan</h2>

          <div className="flex items-center gap-3 text-brand-600">
            <FileText className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="text-sm text-brand-400">Layanan</p>
              <p className="font-medium">{booking.service?.name || "-"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-brand-600">
            <User className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="text-sm text-brand-400">Pelanggan</p>
              <p className="font-medium">{booking.customer?.display_name || "-"}</p>
            </div>
          </div>

          {booking.scheduled_at && (
            <div className="flex items-center gap-3 text-brand-600">
              <Calendar className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm text-brand-400">Jadwal</p>
                <p className="font-medium">{formatDate(booking.scheduled_at)}</p>
              </div>
            </div>
          )}

          {booking.location && (
            <div className="flex items-center gap-3 text-brand-600">
              <MapPin className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm text-brand-400">Lokasi</p>
                <p className="font-medium">{booking.location}</p>
              </div>
            </div>
          )}

          {booking.customer_note && (
            <div className="flex items-center gap-3 text-brand-600">
              <FileText className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm text-brand-400">Catatan Pelanggan</p>
                <p className="font-medium">{booking.customer_note}</p>
              </div>
            </div>
          )}

          <div className="border-t border-brand-50 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brand-400">Estimasi Biaya</p>
                <p className="text-2xl font-extrabold text-brand-800">{formatRupiah(booking.quoted_amount)}</p>
              </div>
              {booking.final_amount && (
                <div className="text-right">
                  <p className="text-sm text-brand-400">Biaya Akhir</p>
                  <p className="text-2xl font-extrabold text-success-600">{formatRupiah(booking.final_amount)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Worker Actions */}
        {booking.status === "PENDING_WORKER" && (
          <div className="card p-6">
            <p className="font-bold text-brand-800 mb-3">Tanggapi Pesanan</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleAction("reject")}
                className="btn-ghost flex-1 border-red-200 text-red-600"
                disabled={actionLoading}
                data-testid="reject-booking"
              >
                <XCircle className="h-4 w-4" /> Tolak
              </button>
              <button
                onClick={() => handleAction("accept")}
                className="btn-primary flex-1"
                disabled={actionLoading}
                data-testid="accept-booking"
              >
                <CheckCircle2 className="h-4 w-4" /> Terima
              </button>
            </div>
          </div>
        )}

        {booking.status === "ACCEPTED" && (
          <div className="card p-6 border-blue-200 bg-blue-50">
            <p className="font-bold text-blue-700 mb-1">Mulai Pekerjaan</p>
            <p className="text-sm text-blue-600 mb-3">Unggah foto kondisi sebelum pengerjaan (wajib).</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-brand-500 mb-1 block">Foto Sebelum</label>
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-brand-200 rounded-xl cursor-pointer hover:border-brand-400 transition bg-white">
                  <Camera className="h-5 w-5 text-brand-400" />
                  <span className="text-sm text-brand-500">
                    {beforeFile ? beforeFile.name : "Pilih foto ( JPG/PNG, max 5MB )"}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => setBeforeFile(e.target.files[0])}
                    data-testid="before-photo-input"
                  />
                </label>
                {beforePreview && (
                  <div className="mt-2 rounded-xl overflow-hidden border border-brand-100">
                    <img src={beforePreview} alt="Preview sebelum" className="w-full max-h-48 object-contain" />
                  </div>
                )}
              </div>
              <button
                onClick={handleStart}
                className="btn-primary w-full"
                disabled={actionLoading || !beforeFile}
                data-testid="start-booking"
              >
                {actionLoading ? "Memproses..." : <span className="flex items-center gap-2"><Play className="h-4 w-4" /> Mulai Kerja</span>}
              </button>
            </div>
          </div>
        )}

        {/* Change Order - Worker Propose */}
        {["ACCEPTED", "ON_PROGRESS"].includes(booking.status) && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-brand-800 text-lg">Ubah Harga/Scope</h2>
              {!showCOForm && (
                <button
                  onClick={() => setShowCOForm(true)}
                  className="btn-ghost !py-2 !px-3 text-sm"
                  data-testid="propose-co-button"
                >
                  <RefreshCw className="h-4 w-4" /> Ajukan Perubahan
                </button>
              )}
            </div>

            {showCOForm && (
              <div className="space-y-3 mb-4 p-4 bg-brand-50 rounded-xl">
                <div>
                  <label className="text-sm text-brand-500 mb-1 block">Deskripsi Perubahan</label>
                  <textarea
                    value={coDescription}
                    onChange={(e) => setCODescription(e.target.value)}
                    placeholder="Jelaskan perubahan yang diusulkan..."
                    rows={2}
                    className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none bg-white"
                    data-testid="co-description"
                  />
                </div>
                <div>
                  <label className="text-sm text-brand-500 mb-1 block">Selisih Harga (Rp, bisa negatif untuk diskon)</label>
                  <input
                    type="number"
                    value={coAmount}
                    onChange={(e) => setCOAmount(e.target.value)}
                    placeholder="contoh: 50000 atau -20000"
                    className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none bg-white"
                    data-testid="co-amount"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowCOForm(false); setCODescription(""); setCOAmount(""); }}
                    className="btn-ghost flex-1 !py-2"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleProposeCO}
                    className="btn-primary flex-1 !py-2"
                    disabled={coLoading || !coDescription.trim() || !coAmount}
                    data-testid="submit-co"
                  >
                    {coLoading ? "Mengirim..." : "Kirim Usulan"}
                  </button>
                </div>
              </div>
            )}

            {/* Change Orders List */}
            {changeOrders.length > 0 && (
              <div className="space-y-3">
                {changeOrders.map((co) => (
                  <div key={co.id} className={`p-4 rounded-xl border ${
                    co.status === "PENDING" ? "border-amber-200 bg-amber-50" :
                    co.status === "ACCEPTED" ? "border-success-200 bg-success-50" :
                    "border-red-200 bg-red-50"
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-brand-800 text-sm">{co.description}</p>
                        <p className={`text-sm font-bold mt-1 ${
                          co.additional_amount >= 0 ? "text-brand-700" : "text-success-600"
                        }`}>
                          {co.additional_amount >= 0 ? "+" : ""}{formatRupiah(co.additional_amount)}
                        </p>
                      </div>
                      <span className={`chip text-xs ${
                        co.status === "PENDING" ? "bg-amber-100 text-amber-700" :
                        co.status === "ACCEPTED" ? "bg-success-100 text-success-700" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {co.status === "PENDING" ? "Menunggu" : co.status === "ACCEPTED" ? "Diterima" : "Ditolak"}
                      </span>
                    </div>
                    {co.customer_note && (
                      <p className="text-xs text-brand-500 mt-2">Catatan pelanggan: {co.customer_note}</p>
                    )}
                    <p className="text-xs text-brand-400 mt-1">
                      {new Date(co.proposed_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment Status */}
        <PaymentStatus bookingId={id} bookingStatus={booking.status} role="worker" />

        {/* Dispute / Appeal */}
        {["ACCEPTED", "ON_PROGRESS", "COMPLETED"].includes(booking.status) && booking.status !== "DISPUTED" && (
          <div className="card p-6 border-amber-200 bg-amber-50">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-brand-800 text-lg">Ajukan Komplain</h2>
              {!showDisputeForm && (
                <button
                  onClick={() => setShowDisputeForm(true)}
                  className="btn-ghost !py-2 !px-3 text-sm border-amber-300 text-amber-700"
                  data-testid="dispute-button"
                >
                  Komplain ke CS
                </button>
              )}
            </div>
            <p className="text-sm text-amber-600 mb-3">
              Jika pelanggan tidak merespon atau ada masalah, ajukan komplain ke customer service.
            </p>

            {showDisputeForm && (
              <div className="space-y-3 p-4 bg-white rounded-xl border border-amber-200">
                <div>
                  <label className="text-sm text-brand-500 mb-1 block">Alasan Komplain</label>
                  <textarea
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="Jelaskan masalah yang terjadi (contoh: pelanggan tidak merespon setelah 24 jam)..."
                    rows={3}
                    className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none"
                    data-testid="dispute-reason"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowDisputeForm(false); setDisputeReason(""); }}
                    className="btn-ghost flex-1 !py-2"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleDispute}
                    className="btn flex-1 !py-2 bg-amber-500 text-white hover:bg-amber-600"
                    disabled={disputeLoading || !disputeReason.trim()}
                    data-testid="submit-dispute"
                  >
                    {disputeLoading ? "Mengirim..." : "Kirim ke CS"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {booking.status === "DISPUTED" && (
          <div className="card p-6 border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-bold text-red-700">Komplain Diajukan</p>
                <p className="text-sm text-red-600">Menunggu review dari Customer Service.</p>
                {booking.dispute_reason && (
                  <p className="text-sm text-red-500 mt-1">Alasan: {booking.dispute_reason}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {booking.status === "ON_PROGRESS" && (
          <div className="card p-6 border-success-200 bg-success-50">
            <p className="font-bold text-success-700 mb-3">Selesaikan Pekerjaan</p>
            <p className="text-sm text-success-600 mb-3">Unggah foto bukti penyelesaian pekerjaan.</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-brand-500 mb-1 block">Foto Bukti</label>
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-brand-200 rounded-xl cursor-pointer hover:border-brand-400 transition bg-white">
                  <Camera className="h-5 w-5 text-brand-400" />
                  <span className="text-sm text-brand-500">
                    {proofFile ? proofFile.name : "Pilih foto ( JPG/PNG, max 5MB )"}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => setProofFile(e.target.files[0])}
                    data-testid="proof-photo-input"
                  />
                </label>
                {proofPreview && (
                  <div className="mt-2 rounded-xl overflow-hidden border border-brand-100">
                    <img src={proofPreview} alt="Preview" className="w-full max-h-48 object-contain" />
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-brand-500 mb-1 block">Catatan Penyelesaian</label>
                <textarea
                  value={completionNote}
                  onChange={(e) => setCompletionNote(e.target.value)}
                  placeholder="Deskripsi pekerjaan yang sudah dilakukan..."
                  rows={2}
                  className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none bg-white"
                  data-testid="completion-note"
                />
              </div>
              <button
                onClick={handleComplete}
                className="btn-success w-full"
                disabled={actionLoading || !proofFile}
                data-testid="complete-booking"
              >
                {actionLoading ? "Memproses..." : <span className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Tandai Selesai</span>}
              </button>
            </div>
          </div>
        )}

        {booking.status === "COMPLETED" && (
          <div className="card p-6 border-brand-200 bg-brand-50" data-testid="worker-waiting-confirm">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-brand-600" />
              <div>
                <p className="font-bold text-brand-800">Menunggu Konfirmasi</p>
                <p className="text-sm text-brand-500">Pelanggan akan mengonfirmasi pekerjaan selesai.</p>
                {booking.auto_confirm_at && (
                  <p className="text-xs text-brand-500 mt-1" data-testid="worker-auto-confirm-countdown">
                    Auto-confirm dalam{" "}
                    <span className="font-bold text-brand-700">
                      {(() => {
                        const ms = Math.max(0, new Date(booking.auto_confirm_at).getTime() - nowTick);
                        const h = Math.floor(ms / 3600000);
                        const m = Math.floor((ms % 3600000) / 60000);
                        const s = Math.floor((ms % 60000) / 1000);
                        return `${h}j ${m}m ${s}d`;
                      })()}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {booking.has_proof && (booking.status === "COMPLETED" || booking.status === "CONFIRMED") && (
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Image className="h-5 w-5 text-success-600" />
              <h2 className="text-brand-800 text-lg">Bukti Sebelum & Sesudah</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-brand-500 mb-2">Sebelum</p>
                {beforeUrl ? (
                  <div className="rounded-xl overflow-hidden border border-brand-100">
                    <img src={beforeUrl} alt="Sebelum pengerjaan" className="w-full max-h-48 object-contain" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-4 bg-brand-50 rounded-xl text-brand-400 text-sm">
                    <Image className="h-6 w-6 mr-1" /> Memuat...
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-brand-500 mb-2">Sesudah</p>
                {proofUrl ? (
                  <div className="rounded-xl overflow-hidden border border-brand-100">
                    <img src={proofUrl} alt="Sesudah pengerjaan" className="w-full max-h-48 object-contain" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-4 bg-brand-50 rounded-xl text-brand-400 text-sm">
                    <Image className="h-6 w-6 mr-1" /> Memuat...
                  </div>
                )}
              </div>
            </div>
            {booking.completion_note && (
              <div className="mt-3 p-3 bg-brand-50 rounded-xl">
                <p className="text-sm text-brand-400">Catatan penyelesaian:</p>
                <p className="text-brand-700 mt-1">{booking.completion_note}</p>
              </div>
            )}
          </div>
        )}

        {booking.status === "CONFIRMED" && (
          <div className="card p-6 border-success-200 bg-success-50">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-success-600" />
              <div>
                <p className="font-bold text-success-700">Pesanan Selesai!</p>
                <p className="text-sm text-success-600">Pelanggan telah mengonfirmasi pekerjaan selesai.</p>
              </div>
            </div>
          </div>
        )}

        {/* Reviews */}
        {booking.status === "CONFIRMED" && (
          <>
            {/* Reviews dari pelanggan untuk pekerja ini */}
            {reviews.filter((r) => r.review_type === "WORKER" && r.reviewer_id !== user?.id).length > 0 && (
              <div className="space-y-3">
                <h2 className="text-brand-800">Review dari Pelanggan</h2>
                {reviews
                  .filter((r) => r.review_type === "WORKER" && r.reviewer_id !== user?.id)
                  .map((r) => (
                    <div key={r.id} className="card p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold text-brand-800 text-sm">{r.reviewer_name || "Pelanggan"}</p>
                        <StarRating rating={r.rating} />
                      </div>
                      {r.comment && <p className="text-sm text-brand-600">{r.comment}</p>}
                      <p className="text-xs text-brand-400 mt-2">{new Date(r.created_at).toLocaleDateString("id-ID")}</p>
                    </div>
                  ))}
              </div>
            )}

            {/* Form untuk pekerja me-review pelanggan */}
            {!reviews.some((r) => r.reviewer_id === user?.id && r.review_type === "CUSTOMER") && (
              <RatingForm
                bookingId={id}
                reviewType="CUSTOMER"
                onReviewed={() => {
                  api.get(`/api/reviews/booking/${id}`).then((r) => setReviews(r.data || []));
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
